<script setup>
import { ref } from 'vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Support',
  },
})

// State untuk form kontak
const form = ref({
  name: '',
  email: '',
  topic: '',
  message: '',
})

const topics = [
  { title: 'Masalah Teknis (Aplikasi error, printer, dll)', value: 'teknis' },
  { title: 'Billing & Langganan', value: 'billing' },
  { title: 'Pertanyaan Fitur', value: 'fitur' },
  { title: 'Lainnya', value: 'lainnya' },
]

const isSubmitting = ref(false)

const submitForm = () => {
  if (!form.value.name || !form.value.email || !form.value.topic || !form.value.message) {
    alert('Mohon lengkapi semua field!')
    return
  }

  isSubmitting.value = true
  
  // Format pesan WhatsApp
  const phoneNumber = '6283895562263' // Ganti dengan nomor WA Support PintuKasir
  const text = `Halo Tim Support PintuKasir,%0A%0A` +
               `*Nama:* ${form.value.name}%0A` +
               `*Email:* ${form.value.email}%0A` +
               `*Topik:* ${form.value.topic}%0A%0A` +
               `*Pesan:*%0A${form.value.message}`
  
  // Buka WhatsApp di tab baru
  setTimeout(() => {
    window.open(`https://wa.me/${phoneNumber}?text=${text}`, '_blank')
    isSubmitting.value = false
    form.value = { name: '', email: '', topic: '', message: '' } // Reset form
  }, 1000)
}

// Data FAQ
const faqs = ref([
  {
    question: 'Bagaimana cara mereset password kasir yang lupa?',
    answer: 'Anda bisa mereset password melalui menu Sistem > User & Role. Klik pada user yang bersangkutan, lalu pilih "Reset Password". Link reset akan dikirim ke email terdaftar.',
  },
  {
    question: 'Printer struk tidak mau mencetak, apa yang harus dilakukan?',
    answer: 'Pastikan printer menyala dan terhubung ke jaringan yang sama. Cek di menu Sistem > Printers, pastikan statusnya "Online". Jika masih gagal, coba restart printer dan refresh halaman POS.',
  },
  {
    question: 'Apakah data saya aman dan bisa di-export?',
    answer: 'Sangat aman. Data Anda dienkripsi dan diisolasi per tenant. Anda bisa meng-export laporan penjualan, inventory, dan laba rugi ke format Excel atau PDF kapan saja melalui menu Laporan.',
  },
  {
    question: 'Bagaimana cara mengaktifkan fitur AI Voice Order?',
    answer: 'Fitur ini tersedia di paket Premium. Aktifkan melalui menu Operasional > AI Voice Order, lalu ikuti panduan kalibrasi mikrofon dan setup bahasa yang tersedia.',
  },
])

// Data kontak cepat
const contactMethods = [
  {
    icon: 'ri-mail-send-line',
    color: 'primary',
    title: 'Email Support',
    value: 'support@pintukasir.com',
    action: 'mailto:support@pintukasir.com',
    desc: 'Balasan dalam 1x24 jam kerja',
  },
  {
    icon: 'ri-whatsapp-line',
    color: 'success',
    title: 'WhatsApp',
    value: '+62 838-9556-2263',
    action: 'https://wa.me/6283895562263',
    desc: 'Respon cepat (Jam kerja)',
  },
  {
    icon: 'ri-time-line',
    color: 'warning',
    title: 'Jam Operasional',
    value: 'Senin - Sabtu, 08.00 - 17.00 WIB',
    action: null,
    desc: 'Minggu & Hari Libur Nasional Tutup',
  },
]
</script>

<template>
  <VContainer fluid class="pa-4 pa-md-6">
    <!-- Header -->
    <VRow class="mb-6">
      <VCol cols="12">
        <div class="text-center text-sm-start">
          <h1 class="text-h4 text-sm-h3 font-weight-bold mb-2">
            🛟 Pusat Bantuan PintuKasir
          </h1>
          <p class="text-body-1 text-medium-emphasis mb-0">
            Ada kendala atau pertanyaan? Tim kami siap membantu Anda.
          </p>
        </div>
      </VCol>
    </VRow>

    <!-- Quick Contact Cards -->
    <VRow class="mb-6">
      <VCol
        v-for="(method, index) in contactMethods"
        :key="index"
        cols="12"
        md="4"
      >
        <VCard 
          variant="outlined" 
          class="h-100 contact-card"
          :class="{ 'cursor-pointer': method.action }"
          @click="method.action && window.open(method.action, '_blank')"
        >
          <VCardText class="text-center py-6">
            <VAvatar :color="method.color" variant="tonal" size="56" class="mb-3">
              <VIcon :icon="method.icon" size="28" />
            </VAvatar>
            <h6 class="text-h6 font-weight-bold mb-1">{{ method.title }}</h6>
            <p class="text-body-1 font-weight-medium mb-1" :class="`text-${method.color}`">
              {{ method.value }}
            </p>
            <p class="text-caption text-medium-emphasis mb-0">
              {{ method.desc }}
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VRow>
      <!-- Left: Contact Form -->
      <VCol cols="12" lg="7" class="mb-6 mb-lg-0">
        <VCard variant="outlined" class="h-100">
          <VCardItem>
            <VCardTitle class="text-h6 font-weight-bold">
              <VIcon icon="ri-edit-box-line" class="me-2" color="primary" />
              Kirim Tiket Bantuan
            </VCardTitle>
            <VCardSubtitle>
              Isi form di bawah ini dan tim kami akan segera menghubungi Anda.
            </VCardSubtitle>
          </VCardItem>

          <VCardText>
            <VForm @submit.prevent="submitForm">
              <VRow>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="form.name"
                    label="Nama Lengkap"
                    prepend-inner-icon="ri-user-3-line"
                    variant="outlined"
                    density="comfortable"
                    required
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="form.email"
                    label="Alamat Email"
                    prepend-inner-icon="ri-mail-line"
                    variant="outlined"
                    density="comfortable"
                    type="email"
                    required
                  />
                </VCol>
                <VCol cols="12">
                  <VSelect
                    v-model="form.topic"
                    :items="topics"
                    label="Topik Bantuan"
                    prepend-inner-icon="ri-question-line"
                    variant="outlined"
                    density="comfortable"
                    required
                  />
                </VCol>
                <VCol cols="12">
                  <VTextarea
                    v-model="form.message"
                    label="Detail Masalah / Pesan"
                    prepend-inner-icon="ri-message-2-line"
                    variant="outlined"
                    density="comfortable"
                    rows="4"
                    placeholder="Jelaskan masalah Anda selengkap mungkin (contoh: error saat cetak struk, dll)"
                    required
                  />
                </VCol>
                <VCol cols="12" class="pt-2">
                  <VBtn
                    type="submit"
                    color="primary"
                    size="large"
                    :loading="isSubmitting"
                    block
                    prepend-icon="ri-send-plane-fill"
                  >
                    Kirim Pesan
                  </VBtn>
                </VCol>
              </VRow>
            </VForm>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Right: FAQ & Quick Links -->
      <VCol cols="12" lg="5">
        <!-- FAQ Section -->
        <VCard variant="outlined" class="mb-6">
          <VCardItem>
            <VCardTitle class="text-h6 font-weight-bold">
              <VIcon icon="ri-question-answer-line" class="me-2" color="secondary" />
              Pertanyaan Umum (FAQ)
            </VCardTitle>
          </VCardItem>
          <VCardText class="pt-0">
            <VExpansionPanels variant="accordion" density="compact">
              <VExpansionPanel
                v-for="(faq, i) in faqs"
                :key="i"
              >
                <VExpansionPanelTitle class="text-body-2 font-weight-medium">
                  {{ faq.question }}
                </VExpansionPanelTitle>
                <VExpansionPanelText class="text-body-2 text-medium-emphasis">
                  {{ faq.answer }}
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>
          </VCardText>
        </VCard>

        <!-- Quick Links -->
        <VCard color="primary" variant="tonal">
          <VCardText>
            <h6 class="text-h6 font-weight-bold mb-3">📚 Butuh panduan lengkap?</h6>
            <div class="d-flex flex-column gap-3">
              <VBtn
                variant="outlined"
                color="primary"
                prepend-icon="ri-book-open-line"
                to="/userguide"
                block
                justify="start"
              >
                Baca User Guide Tertulis
              </VBtn>
              <VBtn
                variant="outlined"
                color="primary"
                prepend-icon="ri-video-line"
                to="/tutorial"
                block
                justify="start"
              >
                Tonton Video Tutorial
              </VBtn>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style lang="scss" scoped>
.contact-card {
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(var(--v-theme-primary), 0.5) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
}

.cursor-pointer {
  cursor: pointer;
}

.gap-3 {
  gap: 12px;
}
</style>
